// src/features/sales-purchase/api/SalesPurchaseApi.ts

import { fetchData } from '../../../../utils/fetch';

export interface SaleItem {
  id_product: number;
  qty: number;
  product_price_purchase: number;
  product_price_sales: number;
  // profits etc come back from the backend but you probably don’t need to send them
}

export interface Sale {
  sale_id: number;
  total_profit: number;
  timestamp: string;
  items: Array<{
    id_product: number;
    qty: number;
    qty_is_int: boolean;
    product_price_purchase: number;
    product_price_sales: number;
    profit: number;
  }>;
}

/**
 * One row of the product report:
 * - timestamp
 * - qty
 * - qty_accumulated
 * - profit
 * - profit_accumulated
 */
export interface ProductReportItem {
  timestamp: string;
  qty: number;
  qty_accumulated: number;
  profit: number;
  profit_accumulated: number;
}

const BASE = '/service_purchase_sales';

/**
 * POST /service_purchase_sales/sale
 * Sends an array of items to be recorded as one sale.
 */
export async function recordSaleApi(
  items: SaleItem[]
): Promise<Sale> {
  const payload = { items };
  const raw = await fetchData(`${BASE}/sale`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  // API error shape?
  if (raw && typeof raw === 'object' && 'error' in raw) {
    throw new Error((raw as any).error as string);
  }

  // Success: assume shape matches Sale
  return raw as Sale;
}

/**
 * GET /service_purchase_sales/sales
 * Optionally pass saleId to filter.
 */
export async function fetchSalesApi(
  saleId?: number
): Promise<Sale[]> {
  const query = saleId != null ? `?sale_id=${saleId}` : '';
  const raw = await fetchData(`${BASE}/sales${query}`, {
    method: 'GET',
  });

  // error?
  if (raw && typeof raw === 'object' && 'error' in raw) {
    throw new Error((raw as any).error as string);
  }

  // raw might be array, or { data: [...] }, or { result: [...] }, or { sales: [...] }
  let arr: any[] = [];
  if (Array.isArray(raw)) {
    arr = raw;
  } else if (raw != null && Array.isArray((raw as any).data)) {
    arr = (raw as any).data;
  } else if (raw != null && Array.isArray((raw as any).result)) {
    arr = (raw as any).result;
  } else if (raw != null && Array.isArray((raw as any).sales)) {
    arr = (raw as any).sales;
  } else {
    console.warn('[fetchSalesApi] unexpected response shape', raw);
    return [];
  }

  return arr as Sale[];
}

/**
 * GET /service_purchase_sales/product_report
 * Required: id_product
 * Optional: date_start, date_end, compression.kind, compression.unit
 */
export async function fetchProductReportApi(
  idProduct: number,
  dateStart?: string,
  dateEnd?: string,
  compression?: {
    kind?: 'Sum' | 'Avg';
    unit?: 'Minute' | 'Hour' | 'Day';
  }
): Promise<ProductReportItem[]> {
  if (typeof idProduct !== 'number' || isNaN(idProduct)) {
    throw new Error('fetchProductReportApi: `idProduct` must be a number');
  }

  // base endpoint + required param
  let endpoint = `${BASE}/product_report?id_product=${encodeURIComponent(idProduct)}`;

  // optional date-range
  if (dateStart) endpoint += `&date_start=${encodeURIComponent(dateStart)}`;
  if (dateEnd)   endpoint += `&date_end=${encodeURIComponent(dateEnd)}`;

  // optional compression (no more `field`)
  if (compression) {
    const { kind, unit } = compression;
    if (kind) endpoint += `&compression.kind=${encodeURIComponent(kind)}`;
    if (unit) endpoint += `&compression.unit=${encodeURIComponent(unit)}`;
  }

  console.debug('[fetchProductReportApi] GET →', endpoint);
  const raw = await fetchData(endpoint, { method: 'GET' });

  // backend wraps results in { report: [...] }
  if (raw && typeof raw === 'object' && Array.isArray((raw as any).report)) {
    return (raw as any).report as ProductReportItem[];
  }

  console.warn('[fetchProductReportApi] unexpected response shape', raw);
  return [];
  }